/**
 * `callFn` — the one way the web client should invoke a Cloud Function
 * (2026-10-28). Wraps `httpsCallable` and records latency + outcome so a
 * user complaint is diagnosable from our own data instead of Cloud Run logs
 * (docs/claude/feature_callable_telemetry.md).
 *
 *   const data = await callFn<Req, Res>('respondToAssignment', { tenantId, assignmentId, decision });
 *
 * Guarantees:
 *   - Returns `result.data` and re-throws the SDK error UNCHANGED — callers
 *     keep their `err.code` / `err.message` / `err.details` handling.
 *   - Telemetry is fire-and-forget: it can never throw into the caller, never
 *     awaits, never delays the call (shaping is synchronous and cheap; the
 *     Firestore write is queued and its rejection swallowed).
 *   - Privacy: records the callable NAME, ms, ok, SDK error code, a scrubbed
 *     error message (failures only), the route with ids replaced, the build,
 *     the browser family and the caller's uid. Never the payload, never the
 *     response, never the raw user agent.
 *   - Sampling: every failure, every call > 5 s, 1 in 20 fast successes
 *     (src/utils/callableTelemetry.ts).
 *   - Skips silently when the uid or tenant is unknown (signed-out, or before
 *     AuthContext has resolved the active tenant — the request's own
 *     `tenantId` field is accepted as a fallback).
 *
 * ☠️ The write imports '@firebase/firestore' directly (like
 * permissionTelemetry) so it does not go through the tracked
 * 'firebase/firestore' alias: a rules denial on client_events must never
 * itself become a permission-denied record.
 */
import { addDoc, collection, serverTimestamp } from '@firebase/firestore';
import { httpsCallable, type HttpsCallableOptions } from 'firebase/functions';

import { auth, db, functions } from '../firebase';
import {
  browserFamily,
  CLIENT_EVENTS_COLLECTION,
  errorCode,
  errorMessage,
  resolveAppVersion,
  shapeEvent,
  shouldSample,
  type BrowserFamily,
} from './callableTelemetry';

export interface CallFnOptions {
  /** Route label for the record; defaults to the current pathname (ids replaced). */
  route?: string;
  /** Tenant for the record; defaults to the AuthContext tenant, then `data.tenantId`. */
  tenantId?: string;
  /**
   * Client-side timeout in ms. The SDK default is 70 s; long-running
   * callables must match the server's timeoutSeconds
   * (docs/claude/feedback_callable_client_timeout.md).
   */
  timeout?: number;
}

const state: {
  tenantId: string | null;
  appVersion: string;
  ua: BrowserFamily | null;
} = {
  tenantId: null,
  appVersion: resolveAppVersion(typeof process !== 'undefined' ? process.env : undefined),
  ua: null,
};

/** Called by AuthContext whenever the active tenant changes (null when signed out). */
export function setCallableTelemetryTenant(tenantId: string | null | undefined): void {
  state.tenantId = typeof tenantId === 'string' && tenantId.trim() ? tenantId.trim() : null;
}

function nowMs(): number {
  try {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') return performance.now();
  } catch {
    /* fall through */
  }
  return Date.now();
}

function currentUa(): BrowserFamily {
  if (state.ua) return state.ua;
  let family: BrowserFamily = 'unknown';
  try {
    family = browserFamily(typeof navigator !== 'undefined' ? navigator.userAgent : undefined);
  } catch {
    /* fail-soft */
  }
  state.ua = family;
  return family;
}

function currentRoute(): string | null {
  try {
    return typeof window !== 'undefined' ? window.location.pathname : null;
  } catch {
    return null;
  }
}

function tenantFromData(data: unknown): string | null {
  if (data && typeof data === 'object') {
    const t = (data as { tenantId?: unknown }).tenantId;
    if (typeof t === 'string' && t.trim()) return t.trim();
  }
  return null;
}

interface RecordInput {
  name: string;
  ms: number;
  ok: boolean;
  code?: string;
  msg?: string;
  route?: string;
  tenantId: string | null;
}

/**
 * Shape + sample + queue the write. Synchronous, never throws, never awaited.
 * Exported for tests only.
 */
export function recordCallableEvent(input: RecordInput): boolean {
  try {
    if (!shouldSample({ ok: input.ok, ms: input.ms, random: Math.random() })) return false;
    const uid = auth.currentUser?.uid;
    const tenantId = input.tenantId;
    if (!uid || !tenantId) return false;
    const event = shapeEvent({
      name: input.name,
      ms: input.ms,
      ok: input.ok,
      code: input.code,
      msg: input.msg,
      route: input.route ?? currentRoute(),
      uid,
      appVersion: state.appVersion,
      ua: currentUa(),
    });
    const ref = collection(db, 'tenants', tenantId, CLIENT_EVENTS_COLLECTION);
    addDoc(ref, { ...event, at: serverTimestamp() }).catch((e: unknown) => {
      // eslint-disable-next-line no-console
      console.debug('[callFn] telemetry write skipped', e instanceof Error ? e.message : e);
    });
    return true;
  } catch (e: unknown) {
    // eslint-disable-next-line no-console
    console.debug('[callFn] telemetry skipped', e instanceof Error ? e.message : e);
    return false;
  }
}

/**
 * Invoke a callable Cloud Function and return its `data`. See the module
 * header for the guarantees.
 */
export async function callFn<TReq = unknown, TRes = unknown>(
  name: string,
  data: TReq,
  opts?: CallFnOptions,
): Promise<TRes> {
  const started = nowMs();
  let ok = false;
  let code: string | undefined;
  let msg: string | undefined;
  try {
    const callableOpts: HttpsCallableOptions | undefined =
      opts?.timeout && opts.timeout > 0 ? { timeout: opts.timeout } : undefined;
    const fn = httpsCallable<TReq, TRes>(functions, name, callableOpts);
    const result = await fn(data);
    ok = true;
    return result.data;
  } catch (err: unknown) {
    code = errorCode(err);
    msg = errorMessage(err);
    throw err;
  } finally {
    recordCallableEvent({
      name,
      ms: nowMs() - started,
      ok,
      code,
      msg,
      route: opts?.route,
      tenantId: opts?.tenantId?.trim() || state.tenantId || tenantFromData(data),
    });
  }
}

/** Test-only reset. */
export function __resetCallableTelemetryForTests(): void {
  state.tenantId = null;
  state.ua = null;
}
